<template>
    <v-container grid-list-xl>
        <v-layout my-6>
            <v-btn @click="$router.go(-1)" class="my-auto">
                <v-icon>navigate_before</v-icon>
                {{ $t('back') }}
            </v-btn>

            <v-flex ml-12 xs10 class="my-auto">
                <div class="display-3 font-weight-thin">{{ $i18n.t('questions') }}</div>
            </v-flex>

            <v-spacer/>
            
            <v-flex lg3 md7 mt-7 mr-8>
                <v-text-field
                    dense
                    outlined
                    v-model="search"
                    @input="searchUpdate"
                    @keydown.enter="showById"
                    :rules="searchRules"
                    :placeholder="$i18n.t('searchQuestion')"
                    :hint="$i18n.t('searchQuestionTooltip')"
                    class="body-1 font-weight-medium"
                    ref="searchField"/>
            </v-flex>

            <v-btn @click="createTopic" class="my-auto mx-4" color="teal">
                {{ $t('addTopic') }}
                <v-icon right>add</v-icon>
            </v-btn>
        </v-layout>

        <v-expansion-panels v-model="activeTopic">
            <v-expansion-panel v-for="topic in getTopics" :key="topic.id" @click="viewTopic(topic)">
                <v-expansion-panel-header>
                    <v-row>
                        <div class="mx-3 title">
                            {{ $i18n.t('TOPIC') + ' ' + topic.position }}
                        </div>
                        <div class="my-auto subtitle-1">
                            {{ topic.name }}
                        </div>
                        <v-spacer/>
                        <div class="my-auto mr-4">
                            {{ topic.numberOfQuestions + ' ' + (topic.numberOfQuestions == 1 ? $i18n.t('question') : $i18n.t('questions')) }}
                        </div>
                    </v-row>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                    <v-layout row align-right py-6>
                        <v-spacer/>

                        <v-btn @click="editTopic(topic)" class="my-auto mx-4" color="teal">
                            {{ $t('editTopic') }}
                            <v-icon right>edit</v-icon>
                        </v-btn>

                        <v-btn @click="createQuestion(topic.id)" class="my-auto mx-4" color="teal">
                            {{ $t('addQuestion') }}
                            <v-icon right>add</v-icon>
                        </v-btn>
                    </v-layout>

                    <v-simple-table class="ml-7">
                        <template v-slot:default>
                            <tbody>
                                <tr v-for="question in getQuestions" :key="question.id" @click="viewQuestion(question)">
                                    <td class="subtitle-1 font-weight-light">{{ question.id }}</td>
                                    <td width="80%">{{ question.title || question.text }}</td>

                                    <v-tooltip open-on-hover open-delay=200 max-width=220 bottom>
                                        <template v-slot:activator="{ on }">
                                            <td v-on="question.hidden ? on : ''" class="subtitle-1 font-weight-light blue-grey--text">{{ question.hidden ? $i18n.t('PROVISIONAL') : '' }}</td>
                                        </template>
                                        <span>{{ $i18n.t('provisionalQuestion') }}</span>
                                    </v-tooltip>

                                    <v-tooltip open-on-hover open-delay=200 max-width=220 bottom>
                                        <template v-slot:activator="{ on }">
                                            <td v-on="question.image ? on : ''" class="subtitle-1 font-weight-light brown--text">{{ question.image ? $i18n.t('IMAGE') : '' }}</td>
                                        </template>
                                        <span>{{ $i18n.t('imageTooltip') }}</span>
                                    </v-tooltip>

                                    <v-tooltip open-on-hover open-delay=200 max-width=220 bottom>
                                        <template v-slot:activator="{ on }">
                                            <td v-on="question.basic==false ? on : ''" class="subtitle-1 font-weight-light deep-orange--text">{{ question.basic == false ? $i18n.t('ADVANCED') : '' }}</td>
                                        </template>
                                        <span>{{ $i18n.t('advancedTooltip') }}</span>
                                    </v-tooltip>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <v-dialog v-model="viewQuestionDialog" max-width="1300px">
            <EditQuestion v-if="isEditingQuestion"/>
            <QuestionInfo v-else/>
        </v-dialog>

        <v-dialog v-model="createQuestionDialog" max-width="1300px">
            <CreateQuestion/>
        </v-dialog>

        <v-dialog v-model="createTopicDialog" max-width="1300px">
            <CreateTopic/>
        </v-dialog>

        <v-dialog v-model="editTopicDialog" max-width="1300px">
            <EditTopic/>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import QuestionInfo from '@/components/Question/QuestionInfo'
import EditQuestion from '@/components/Question/EditQuestion'
import CreateQuestion from '@/components/Question/CreateQuestion'
import CreateTopic from '@/components/Topic/CreateTopic'
import EditTopic from '@/components/Topic/EditTopic'

export default {
    data() {
        return {
            search: null,
            questionExists: true,
            questionInSubject: true,

            searchRules: [
                v => this.questionExists || this.$i18n.t('noQuestionFound'),
                v => this.questionInSubject || this.$i18n.t('questionNotInSubject')
            ],
        }
    },

    methods: {
        viewTopic(topic){
            this.$store.dispatch('getQuestionsByTopic', { topicId: topic.id })
        },
        viewQuestion(question){
            this.$store.commit('setEditingQuestion', false)
            this.$store.commit('setRemovingQuestion', false)
            this.$store.commit('setQuestion', question)
            this.$store.dispatch('getFormulationsByQuestion', question.id)
            if (question.image)
                this.$store.dispatch('getImage', question.id)

            this.$store.commit('setViewQuestionDialog', true)
        },
        createQuestion(topicId){
            this.$store.commit('newQuestion', topicId)
            this.$store.commit('setCreateQuestionDialog', true)
        },
        createTopic(){
            this.$store.commit('newTopic')
            this.$store.commit('setCreateTopicDialog', true)
        },
        editTopic(topic){
            this.$store.commit('setTopic', topic)
            this.$store.commit('resetTopicValues')
            this.$store.commit('setRemovingTopic', false)
            this.$store.commit('setEditTopicDialog', true)
        },
        searchUpdate(){
            this.questionExists = true
            this.questionInSubject = true
            this.$refs.searchField.validate()
        },
        showById(){
            this.$store.dispatch('getQuestion', this.search)
            .then((question) => {
                if (question.subjectId == this.getCurrentSubjectId) {
                    this.activeTopic = undefined
                    this.$store.commit('setQuestions', [question])
                    this.viewQuestion(question)
                } else {
                    this.questionInSubject = false
                    this.$refs.searchField.validate()
                }
            })
            .catch(() => {
                this.questionExists = false
                this.$refs.searchField.validate()
            })
        }
    },

    created(){
        this.$store.dispatch('getTopics')
    },

    computed: {
        ...mapGetters([
            'isAdmin',
            'getTopics',
            'getQuestions',
            'getViewQuestionDialog',
            'getCreateQuestionDialog',
            'getCreateTopicDialog',
            'getEditTopicDialog',
            'isEditingQuestion',
            'getQuestion',
            'getActiveTopic',
            'getCurrentSubjectId',
        ]),
        viewQuestionDialog: {
            get() { return this.getViewQuestionDialog },
            set(value) { this.$store.commit('setViewQuestionDialog', value) },
        },
        createQuestionDialog: {
            get() { return this.getCreateQuestionDialog },
            set(value) { this.$store.commit('setCreateQuestionDialog', value) },
        },
        createTopicDialog: {
            get() { return this.getCreateTopicDialog },
            set(value) { this.$store.commit('setCreateTopicDialog', value) },
        },
        editTopicDialog: {
            get() { return this.getEditTopicDialog },
            set(value) { this.$store.commit('setEditTopicDialog', value) },
        },
        activeTopic: {
            get() { return this.getActiveTopic },
            set(value) { this.$store.commit('setActiveTopic', value) },
        }
    },

    components: {
        QuestionInfo,
        EditQuestion,
        CreateQuestion,
        CreateTopic,
        EditTopic,
    },
}
</script>
